/* eslint-disable react/jsx-props-no-spreading */
import React, { useEffect, useState } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import {
  Loading,
  Pecten,
  Sizes,
} from '@sede-enterprise/shell-ds-react-framework';
import axios from 'axios';
import { FormProvider, useForm } from 'react-hook-form';
import { AuthWrapper } from './style';
import { IFullState } from '../../types/All.type';
import routepaths from '../../router/routepaths';
import {
  fetchGenesisRolePermission,
  userRoleActions,
} from '../../store/reducers/GenesisUser';
import { GET_TOWER_PC_USERS } from '../../constants/UserAndRoles';
import { ERR_CODE_403 } from '../../utils/common/utils';
import FormControlSelect from '../../UI/FormComponent/FormControlSelect';
import Button from '../../UI/Button';
import { getEnv } from 'src/utils/env.vite';

interface ITowerPcUsers {
  emailId: string;
  shortId: string;
  userName: string;
}

const Authenticate = () => {
  const methods = useForm();

  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [isTowerPc, setIsTowerPc] = useState<boolean>(false);
  const [towerPcUsers, setTowerPcUsers] = useState<ITowerPcUsers[]>([]);
  const userState = useSelector(
    (state: IFullState) => state.genesisUserRolePermission
  );
  const navigate = useNavigate();
  const dispatch = useDispatch();

  const handleSubmitTowerPCUser = (
    userName: string,
    users: ITowerPcUsers[]
  ) => {
    setIsTowerPc(false);
    setIsLoading(true);
    const userDetails = users.find((_) => _.userName === userName);
    if (!userDetails) {
      navigate(routepaths.authError);
      return;
    }
    sessionStorage.setItem('tower-pc-user', userDetails.userName);
    dispatch(
      userRoleActions.setTowerPcUserDetails({
        mail: userDetails.emailId,
        uid: userDetails.shortId,
        userName: userDetails.userName,
      })
    );
    dispatch(
      fetchGenesisRolePermission({
        accessToken: userState.accessToken,
        userName,
      })
    );
  };

  const handleCheckTowerPCUser = async () => {
    try {
      const { data } = (await axios.get(
        `${getEnv('VITE_APP_GEN_API_BASE_URL') ?? ''}${GET_TOWER_PC_USERS}`,
        {
          headers: { Authorization: `Bearer ${userState.accessToken}` },
        }
      )) || { genericUsers: [], towerPcUser: [] };

      const towerPCUserName = sessionStorage.getItem('tower-pc-user');
      if (towerPCUserName) {
        handleSubmitTowerPCUser(towerPCUserName, data.towerPcUser);
        return;
      }

      // check if tower pc user
      const genericUsers = data.genericUsers.map(({ emailId }) =>
        emailId?.toLowerCase()
      );
      if (genericUsers.includes(userState.mail?.toLowerCase())) {
        setIsLoading(false);
        setTowerPcUsers(data?.towerPcUser || []);
        setIsTowerPc(true);
      } else {
        dispatch(
          fetchGenesisRolePermission({
            accessToken: userState.accessToken,
          })
        );
      }
    } catch (e) {
      if (
        axios.isAxiosError(e) &&
        e.request?.status === 0 &&
        e.response === undefined
      ) {
        navigate(routepaths.blockedAddress);
      } else {
        navigate(routepaths.authError);
      }
    }
  };

  useEffect(() => {
    if (userState.isFetching) {
      return;
    }

    // check if IP address is not white listed or blocked
    if (userState.error === 'CORS') {
      navigate(routepaths.blockedAddress);
      return;
    }

    // check if user is inactive from Genesis
    if (userState.error === String(ERR_CODE_403)) {
      navigate(routepaths.notAuthorized);
      return;
    }

    if (userState.isAuthorized) {
      const path = sessionStorage.getItem('redirectPath') ?? '/dashboard';
      sessionStorage.removeItem('redirectPath');
      setIsLoading(false);
      const timeOut = 2000;
      setTimeout(() => {
        navigate(path);
      }, timeOut);
    } else if (userState.isAuthorized === null) {
      if (userState.accessToken === '') {
        navigate(routepaths.base);
        return;
      }
      handleCheckTowerPCUser();
    } else {
      navigate(routepaths.authError);
    }
  }, [userState, dispatch, navigate]);

  const handleOnSubmit = (formData) => {
    handleSubmitTowerPCUser(formData.userName, towerPcUsers);
  };

  return (
    <AuthWrapper>
      <Pecten size={Sizes.Large} />
      <h2>Genesis</h2>
      {isTowerPc ? (
        <div className='tower-pc__container'>
          <FormProvider {...methods}>
            <div>
              <FormControlSelect
                controllerProps={{
                  name: 'userName',
                  rules: {
                    required: 'User is required!',
                  },
                }}
                label=''
                isLoading={false}
                data={towerPcUsers.map((_) => ({
                  id: _.userName,
                  name: _.userName,
                }))}
                methods={methods}
                selectProps={{
                  allowClear: false,
                  placeholder: 'Select User',
                  size: 'small',
                  className: 'select-user',
                }}
              />
            </div>
          </FormProvider>
          <Button size='small' onClick={methods.handleSubmit(handleOnSubmit)}>
            Authenticate
          </Button>
        </div>
      ) : null}
      {isLoading && (
        <>
          <Loading size='medium' className='loader' />
          <div className='login-message'>Loading...</div>
        </>
      )}
      {userState.isAuthorized && (
        <div className='auth-successfully'>
          <div className='user-name'>{`Hi ${userState.userName},`}</div>
          <div className='welcome-message'>Welcome to Genesis</div>
          <Loading size='medium' className='loader' />
          <div className='success-message'>
            Authentication successful. Logging you in ...
          </div>
        </div>
      )}
    </AuthWrapper>
  );
};

export default Authenticate;
